import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  Button,
  IconButton,
  Typography,
  TextField,
  Autocomplete,
  Box,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { AiOutlineClose } from "react-icons/ai";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

export default function FilterDrawer({ filterModal, setFilterModal, dateRange, setDateRange, country, selectedCountry, setSelectedCountry, onFilter, locations, selectedLoc, setSelectedLoc, filterLocation, setFilterLocation, canViewLocation, canViewDashboard, handleCloseFilter}) {

  const [rangeType, setRangeType] = useState("custom");
  const [rangeLabel, setRangeLabel] = useState("");
  const countryTimeoutRef = useRef(null);
  const locTimeoutRef = useRef(null);

  const [localDateRange, setLocalDateRange] = useState(dateRange);
  const [localSelectedCountry, setLocalSelectedCountry] = useState(selectedCountry);
  const [localSelectedLoc, setLocalSelectedLoc] = useState(selectedLoc);

  useEffect(() => {
    if (filterModal) {
      setLocalDateRange(dateRange);
      setLocalSelectedCountry(selectedCountry);
      setLocalSelectedLoc(selectedLoc);
    }
  }, [filterModal, dateRange, selectedCountry, selectedLoc]);

  useEffect(() => {
    return () => {
      if (countryTimeoutRef.current) clearTimeout(countryTimeoutRef.current);
      if (locTimeoutRef.current) clearTimeout(locTimeoutRef.current);
    };
  }, []);

  // ✅ Default range — from Jan 1, 2024 to today
  useEffect(() => {
    const today = dayjs();
    const defaultStart = dayjs("2024-01-01");
    const defaultEnd = today;

    if (!localDateRange.start && !localDateRange.end) {
      setLocalDateRange({ start: defaultStart, end: defaultEnd });
    }
  }, []);

  useEffect(() => {
    const today = dayjs();
    let start, end;

    switch (rangeType) {
      case "thisMonth":
        start = today.startOf("month");
        end = today;
        break;
      case "lastMonth":
        start = today.subtract(1, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
        break;
      case "last3Months":
        start = today.subtract(3, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
        break;
      case "last6Months":
        start = today.subtract(6, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
        break;
      case "last12Months":
        start = today.subtract(12, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
        break;
      case "custom":
        start = localDateRange.start || dayjs("2024-01-01");
        end = localDateRange.end || today;
        break;
      default:
        start = dayjs("2024-01-01");
        end = today;
    }

    if (rangeType !== "custom") {
      setLocalDateRange({ start, end });
    }
    setRangeLabel(`From: ${start.format("DD MMM YYYY")} → ${end.format("DD MMM YYYY")}`);
  }, [rangeType]);

  const handleReset = () => {
    const today = dayjs();
    setRangeType("custom");
    setLocalDateRange({ start: dayjs("2024-01-01"), end: today });
    setLocalSelectedCountry("All");
    setLocalSelectedLoc("All");
    setFilterLocation(["All"]);
  };

  const handleApply = () => {
    setDateRange(localDateRange);
    setSelectedCountry(localSelectedCountry);
    setSelectedLoc(localSelectedLoc);
    handleCloseFilter();
  };

  return (
    <Drawer anchor="right" open={filterModal} onClose={handleCloseFilter}>
      <Box width={320} p={3}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Filters</Typography>
          <IconButton onClick={handleCloseFilter}>
            <AiOutlineClose />
          </IconButton>
        </Box>
        <Stack spacing={2}>
          {/* 📅 Date Range Filter */}
          {canViewDashboard && (
            <>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  label="Date Range"
                  value={rangeType}
                  onChange={(e) => setRangeType(e.target.value)}
                >
                  <MenuItem value="lastMonth">Last Month</MenuItem>
                  <MenuItem value="last3Months">Last 3 Months</MenuItem>
                  <MenuItem value="last6Months">Last 6 Months</MenuItem>
                  <MenuItem value="last12Months">Last 12 Months</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
                {rangeLabel && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      mb: 1,
                      textAlign: "right",
                      fontStyle: "italic",
                    }}
                  >
                    {rangeLabel}
                  </Typography>
                )}
              </FormControl>

              {rangeType === "custom" && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack spacing={1}>
                    <DatePicker
                      label="Start Date"
                      value={localDateRange.start}
                      maxDate={dayjs()}
                      onChange={(newValue) =>
                        setLocalDateRange({ ...localDateRange, start: newValue })
                      }
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" fullWidth />
                      )}
                    />
                    <DatePicker
                      label="End Date"
                      value={localDateRange.end}
                      minDate={localDateRange.start}
                      maxDate={dayjs()}
                      onChange={(newValue) => setLocalDateRange({ ...localDateRange, end: newValue })}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" fullWidth />
                      )}
                    />
                  </Stack>
                </LocalizationProvider>
              )}
            </>
          )}

          {/* 🌍 Country + Location Filters */}
          {canViewLocation && (
            <>
              <Autocomplete
                options={country}
                value={localSelectedCountry}
                onInputChange={(event, newInputValue, reason) => {
                  if (countryTimeoutRef.current) {
                    clearTimeout(countryTimeoutRef.current);
                  }
                  countryTimeoutRef.current = setTimeout(() => {
                    if (reason === "clear") {
                      setLocalSelectedCountry("All");
                      setLocalSelectedLoc("All");
                      setFilterLocation(["All"]);
                    } else {
                      const locList = ["All"];
                      locations?.forEach((el) => {
                        const cntry = el?.updated_country || el?.country || "N/A";
                        if (cntry.toLowerCase() === newInputValue.toLowerCase()) {
                          locList.push(el.location_id);
                        }
                      });
                      setFilterLocation(locList);
                      setLocalSelectedLoc("All");
                      setLocalSelectedCountry(newInputValue);
                    }
                  }, 500);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Country" variant="outlined" fullWidth />
                )}
              />

              <Autocomplete
                options={filterLocation}
                value={localSelectedLoc}
                disabled={localSelectedCountry === "All"}
                onInputChange={(event, newInputValue, reason) => {
                  if (locTimeoutRef.current) {
                    clearTimeout(locTimeoutRef.current);
                  }
                  locTimeoutRef.current = setTimeout(() => {
                    if (reason === "clear") {
                      setLocalSelectedLoc("All");
                    } else {
                      setLocalSelectedLoc(newInputValue);
                    }
                  }, 500);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Location" variant="outlined" fullWidth />
                )}
              />
            </>
          )}

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={handleReset}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleApply}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}
